import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './App.css';

// --- Icons ---
const SearchIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"></circle>
    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
  </svg>
);

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const BagIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
    <line x1="3" y1="6" x2="21" y2="6"></line>
    <path d="M16 10a4 4 0 0 1-8 0"></path>
  </svg>
);

const HeartIcon = ({ filled }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill={filled ? "#e74c3c" : "none"} stroke={filled ? "#e74c3c" : "currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
  </svg>
);

const CopyIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
  </svg>
);

const StatsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10"></line>
    <line x1="12" y1="20" x2="12" y2="4"></line>
    <line x1="6" y1="20" x2="6" y2="14"></line>
  </svg>
);

const EditIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
);

const TrashIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
);


// const PlusIcon = () => (
//   <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
//     <line x1="12" y1="5" x2="12" y2="19"></line>
//     <line x1="5" y1="12" x2="19" y2="12"></line>
//   </svg>
// );


// --- Components ---

const Toast = ({ message, isVisible }) => (
  <div className={`toast ${isVisible ? 'visible' : ''}`}>
    {message}
  </div>
);

const SkeletonCard = () => (
  <div className="bag-card skeleton-card">
    <div className="card-image-wrapper skeleton-image"></div>
    <div className="card-content">
      <div className="skeleton-text title"></div>
      <div className="skeleton-text tag"></div>
    </div>
  </div>
);

function App() {
  const [bags, setBags] = useState([]);
  const [filteredBags, setFilteredBags] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBag, setSelectedBag] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isAdding, setIsAdding] = useState(false); // Controls the Add Modal
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [favorites, setFavorites] = useState(new Set());
  const [toast, setToast] = useState({ message: '', visible: false });
  const [showFavorites, setShowFavorites] = useState(false);
  const [filterType, setFilterType] = useState('ALL'); // 'ALL', 'GAB', 'GSB'
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const API_BASE = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000';

  // New Bag State (for Add/Edit)
  const [formData, setFormData] = useState({
    ref: '', color: '', colorBand: '', DT: '', DCH: '', 
    poidSac: '', poidMC: '', long: '', larg: '', 
    bondeletteT: '', bondeletteCH: ''
  });
  const [imageFile, setImageFile] = useState(null);

  // Load Store data
  const fetchData = useCallback(async () => {
     setLoading(true);
     fetch(`/articles.json?t=${new Date().getTime()}`)
        .then(response => {
          if (!response.ok) throw new Error('Failed to fetch data');
          return response.json();
        })
        .then(data => {
          if (Array.isArray(data)) {
            setBags(data);
            setFilteredBags(data);
          } else {
            throw new Error('Invalid data format: Expected an array');
          }
          setLoading(false);
        })
        .catch(err => {
          console.error("Error:", err);
          setError(err.message);
          setLoading(false);
        });
  }, []); // Dependencies for fetchData

  useEffect(() => {
    // Simulate initial delay purely for smoother UI on first load
    const timer = setTimeout(() => fetchData(), 500); 
    return () => clearTimeout(timer);
  }, [fetchData]); // Dependency array includes fetchData

  // Load Favorites
  useEffect(() => {
    const saved = localStorage.getItem('bag_favorites');
    if (saved) {
      setFavorites(new Set(JSON.parse(saved)));
    }
  }, []);

  // Save Favorites
  const toggleFavorite = (e, id) => {
    if(e) e.stopPropagation();
    const newFavs = new Set(favorites);
    let msg = '';
    if (newFavs.has(id)) {
      newFavs.delete(id);
      msg = 'Removed from favorites';
    } else {
      newFavs.add(id);
      msg = 'Added to favorites';
    }
    setFavorites(newFavs);
    localStorage.setItem('bag_favorites', JSON.stringify([...newFavs]));
    showToast(msg);
  };

  const showToast = (msg) => {
    setToast({ message: msg, visible: true });
    setTimeout(() => setToast(prev => ({ ...prev, visible: false })), 3000);
  };

  // Filter Logic
  useEffect(() => {
    let result = bags;

    if (showFavorites) {
      result = result.filter(bag => favorites.has(bag.id));
    }

    if (filterType !== 'ALL') {
      result = result.filter(bag => bag.ref && bag.ref.toUpperCase().startsWith(filterType));
    }

    if (searchTerm.trim()) {
      const lowerTerm = searchTerm.toLowerCase();
      result = result.filter(bag => 
        (bag.ref && bag.ref.toLowerCase().includes(lowerTerm)) || 
        (bag.color && bag.color.toLowerCase().includes(lowerTerm))
      );
    }

    setFilteredBags(result);
    // Only reset page if search changes significantly results
    if (result.length < (currentPage - 1) * itemsPerPage) {
        setCurrentPage(1);
    }
  }, [searchTerm, bags, favorites, showFavorites, filterType, currentPage, itemsPerPage]);

  // Pagination Logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredBags.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredBags.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      showToast(`Copied: ${text}`);
    });
  };

  const handleBagClick = (bag) => {
    setSelectedBag(bag);
    setIsEditing(false); // Default to view mode
    document.body.style.overflow = 'hidden';
  };

//   const openAddModal = () => {
//     setFormData({
//       ref: '', color: '', colorBand: '', DT: '', DCH: '', 
//       poidSac: '', poidMC: '', long: '', larg: '', 
//       bondeletteT: '', bondeletteCH: ''
//     });
//     setImageFile(null);
//     setIsAdding(true);
//     document.body.style.overflow = 'hidden';
//   };

  const openEditModal = (bag) => {
    setFormData({
      ref: bag.ref || '', 
      color: bag.color || '', 
      colorBand: bag.colorBand || '', 
      DT: bag.DT || '', 
      DCH: bag.DCH || '', 
      poidSac: bag.poidSac || '', 
      poidMC: bag.poidMC || '', 
      long: bag.long || '', 
      larg: bag.larg || '', 
      bondeletteT: bag.bondeletteT || '', 
      bondeletteCH: bag.bondeletteCH || ''
    });
    setImageFile(null);
    setIsEditing(true);
    // We keep selectedBag active because the edit form is displayed inside the modal
  };

  const closeModal = () => {
    setSelectedBag(null);
    setIsAdding(false);
    setIsEditing(false);
    setShowDeleteConfirm(false);
    document.body.style.overflow = 'auto';
  };

  // --- CRUD Operations ---

  const handleFileChange = (e) => {
    if(e.target.files && e.target.files[0]) {
        setImageFile(e.target.files[0]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitAdd = async (e) => {
    e.preventDefault();
    const data = new FormData();
    Object.keys(formData).forEach(key => data.append(key, formData[key]));
    if(imageFile) data.append('image', imageFile);

    try {
      const res = await fetch(`${API_BASE}/api/articles`, {
        method: 'POST',
        body: data
      });
      if(!res.ok) throw new Error('Failed to add');
      
      showToast('Bag added successfully!');
      closeModal();
      fetchData(); // Refresh list
    } catch (err) {
      console.error(err);
      showToast('Error adding bag');
    }
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    const data = new FormData();
    Object.keys(formData).forEach(key => data.append(key, formData[key]));
    if(imageFile) data.append('image', imageFile);

    try {
      const res = await fetch(`${API_BASE}/api/articles/${selectedBag.id}`, {
        method: 'PUT',
        body: data
      });
      if(!res.ok) throw new Error('Failed to update');

      showToast('Bag updated successfully!');
      // Update the selected bag in memory so the view reflects changes immediately
      // Note: Image update won't show immediately without fetching or returning new URL from backend
      // So we just fetch everything again to be safe
      fetchData(); 
      setIsEditing(false); // Switch back to view mode
    } catch (err) {
      console.error(err);
      showToast('Error updating bag');
    }
  };

  const handleDeleteClick = () => {
     setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/articles/${selectedBag.id}`, {
        method: 'DELETE'
      });
      if(!res.ok) throw new Error('Failed to delete');

      showToast('Bag deleted successfully');
      closeModal(); // This now also closes delete confirm
      fetchData();
    } catch (err) {
      console.error(err);
      showToast('Error deleting bag');
    }
  };


  // Stats
  const stats = useMemo(() => {
    const total = bags.length;
    const colors = new Set(bags.map(b => b.color)).size;
    return { total, colors };
  }, [bags]);

  if (error) return (
    <div className="error-screen">
      <div className="error-icon">!</div>
      <h2>System Error</h2>
      <p>{error}</p>
      <button className="primary-btn" onClick={() => window.location.reload()}>Retry Connection</button>
    </div>
  );

  return (
    <div className="App">
      <Toast message={toast.message} isVisible={toast.visible} />
      
      <div className="hero-section">
        <div className="hero-pattern"></div>
        <nav className="navbar">
          <div className="logo">
            <BagIcon />
            <span>Norgrainse<span className="pro-badge">PRO</span></span>
          </div>
          <div className="nav-actions">
            <button 
              className={`nav-btn ${showFavorites ? 'active' : ''}`}
              onClick={() => setShowFavorites(!showFavorites)}
            >
              <HeartIcon filled={showFavorites} />
              <span className="desktop-only">Favorites ({favorites.size})</span>
            </button>
          </div>
        </nav>
        
        <div className="hero-content">
          <h1>Internal Reference Library</h1>
          <p>Instant access to technical specifications and assembly details.</p>
          
          <div className="search-container-pro">
            <div className={`search-wrapper ${searchTerm ? 'active' : ''}`}>
              <div className="search-icon"><SearchIcon /></div>
              <input
                type="text"
                placeholder="Search reference (e.g. GAB 15) or color..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              {searchTerm && (
                <button className="clear-search" onClick={() => setSearchTerm('')}>
                  &times;
                </button>
              )}
            </div>
            
            <div className="filter-tabs">
              <button 
                className={`filter-tab ${filterType === 'ALL' ? 'active' : ''}`}
                onClick={() => setFilterType('ALL')}
              >
                All Types
              </button>
              <button 
                className={`filter-tab ${filterType === 'GAB' ? 'active' : ''}`}
                onClick={() => setFilterType('GAB')}
              >
                GAB Series
              </button>
              <button 
                className={`filter-tab ${filterType === 'GSB' ? 'active' : ''}`}
                onClick={() => setFilterType('GSB')}
              >
                GSB Series
              </button>
            </div>
          </div>

          {!loading && (
            <div className="stats-bar">
              <div className="stat-item">
                <StatsIcon />
                <span><strong>{stats.total}</strong> Total References</span>
              </div>
              <div className="stat-separator">•</div>
              <div className="stat-item">
                <span><strong>{stats.colors}</strong> Unique Variations</span>
              </div>
            </div>
          )}
        </div>
      </div>

      <main className="main-content">
        <div className="results-header">
          <h2>
            {showFavorites ? 'Favorite Items' : (searchTerm ? 'Search Results' : 'Master Collection')}
            {!loading && <span className="count-badge">{filteredBags.length}</span>}
          </h2>
          {/* <button className="add-btn tertiary-add-btn" onClick={openAddModal}>
             <PlusIcon /> Add New
          </button> */}
        </div>

        <div className="bags-grid">
          {loading ? (
            // Skeleton Loading State
            Array(8).fill(0).map((_, i) => <SkeletonCard key={i} />)
          ) : currentItems.length > 0 ? (
            currentItems.map((bag) => (
              <div key={bag.id} className="bag-card" onClick={() => handleBagClick(bag)}>
                <div className="card-image-wrapper">
                  <img 
                    src={bag.img && bag.img.startsWith('http') ? bag.img : (bag.img ? `/${bag.img}` : '')}
                    alt={bag.ref} 
                    className="bag-thumbnail"
                    loading="lazy"
                    onError={(e) => {
                      e.target.onerror = null; 
                      e.target.src = `https://ui-avatars.com/api/?name=${bag.ref}&background=f0f0f0&color=aaa&size=200`
                    }}
                  />
                  <div className="card-overlay">
                    <span className="view-btn">View Specs</span>
                  </div>
                  <button 
                    className="favorite-btn" 
                    onClick={(e) => toggleFavorite(e, bag.id)}
                  >
                    <HeartIcon filled={favorites.has(bag.id)} />
                  </button>
                </div>
                <div className="card-content">
                  <div className="card-header">
                    <h3>{bag.ref}</h3>
                  </div>
                  <div className="card-footer">
                   <span className="color-dot" style={{
                      backgroundColor: bag.color && bag.color.toLowerCase().includes('rouge') ? '#e74c3c' : 
                                     bag.color && bag.color.toLowerCase().includes('bleu') ? '#3498db' : 
                                     bag.color && bag.color.toLowerCase().includes('jaune') ? '#f1c40f' : 
                                     bag.color && bag.color.toLowerCase().includes('vert') ? '#2ecc71' : 
                                     bag.color && bag.color.toLowerCase().includes('noir') ? '#2d3436' : '#95a5a6'
                    }}></span>
                   <span className="color-name">{bag.color || 'Unknown'}</span>
                  </div>
                </div>
              </div>
            ))
          ) : (
             <div className="empty-state">
              <div className="empty-icon"><SearchIcon /></div>
              <h3>No references found</h3>
              <p>Try adjusting your search terms or filters.</p>
              {showFavorites ? (
                <button className="text-btn" onClick={() => setShowFavorites(false)}>View all items</button>
              ) : (
                <button className="text-btn" onClick={() => setSearchTerm('')}>Clear search</button>
              )}
            </div>
          )}
        </div>

        {!loading && filteredBags.length > itemsPerPage && (
          <div className="pagination">
            <button 
              className="page-btn" 
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Prev
            </button>
            <span className="page-info">
              Page {currentPage} of {totalPages}
            </span>
            <button 
              className="page-btn" 
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        )}
      </main>

      {/* --- ADD MODAL --- */}
      {isAdding && (
         <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content form-modal" onClick={(e) => e.stopPropagation()}>
               <div className="modal-close-wrapper">
                  <button className="close-button" onClick={closeModal}><CloseIcon /></button>
               </div>
               <div className="modal-body">
                  <h2 className="form-title">Add New Bag</h2>
                  <form className="bag-form" onSubmit={handleSubmitAdd}>
                     
                     <div className="form-section">
                        <h4 className="section-header">Basic Information</h4>
                        <div className="form-grid">
                           <div className="form-group full-width">
                              <label>Reference (REF) *</label>
                              <input name="ref" value={formData.ref} onChange={handleInputChange} required placeholder="e.g. GAB 15" />
                           </div>
                           <div className="form-group">
                              <label>Color</label>
                              <input name="color" value={formData.color} onChange={handleInputChange} placeholder="e.g. Blue" />
                           </div>
                           <div className="form-group">
                              <label>Color Band</label>
                              <input name="colorBand" value={formData.colorBand} onChange={handleInputChange} placeholder="e.g. Red" />
                           </div>
                        </div>
                     </div>

                     <div className="form-section">
                        <h4 className="section-header">Dimensions & Weights</h4>
                        <div className="form-grid">
                           <div className="form-group">
                              <label>Length (cm)</label>
                              <input name="long" value={formData.long} onChange={handleInputChange} />
                           </div>
                           <div className="form-group">
                              <label>Width (cm)</label>
                              <input name="larg" value={formData.larg} onChange={handleInputChange} />
                           </div>
                           <div className="form-group">
                              <label>Bag Weight (g)</label>
                              <input name="poidSac" value={formData.poidSac} onChange={handleInputChange} />
                           </div>
                           <div className="form-group">
                              <label>MC Weight (g)</label>
                              <input name="poidMC" value={formData.poidMC} onChange={handleInputChange} />
                           </div>
                        </div>
                     </div>

                     <div className="form-section">
                        <h4 className="section-header">Technical Specs</h4>
                        <div className="form-grid">
                           <div className="form-group">
                              <label>DT</label>
                              <input name="DT" value={formData.DT} onChange={handleInputChange} />
                           </div>
                           <div className="form-group">
                              <label>DCH</label>
                              <input name="DCH" value={formData.DCH} onChange={handleInputChange} />
                           </div>
                           <div className="form-group">
                              <label>Bondelette T</label>
                              <input name="bondeletteT" value={formData.bondeletteT} onChange={handleInputChange} />
                           </div>
                           <div className="form-group">
                              <label>Bondelette CH</label>
                              <input name="bondeletteCH" value={formData.bondeletteCH} onChange={handleInputChange} />
                           </div>
                        </div>
                     </div>

                     <div className="form-section">
                        <h4 className="section-header">Visuals</h4>
                        <div className="file-upload-wrapper">
                           <input type="file" id="add-file" accept="image/*" onChange={handleFileChange} className="file-input-hidden" />
                           <label htmlFor="add-file" className="file-upload-label">
                              {imageFile ? (
                                <span className="file-name">{imageFile.name}</span>
                              ) : (
                                <span><span className="upload-icon">📷</span> Choose Validation Image</span>
                              )}
                           </label>
                        </div>
                     </div>

                     <div className="form-actions">
                        <button type="button" className="cancel-btn" onClick={closeModal}>Cancel</button>
                        <button type="submit" className="save-btn">Create Bag</button>
                     </div>
                  </form>
               </div>
            </div>
         </div>
      )}

      {/* --- VIEW / EDIT MODAL --- */}
      {selectedBag && !isAdding && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className={`modal-content ${isEditing ? 'form-modal' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className="modal-close-wrapper">
               <button className="close-button" onClick={closeModal}>
                <CloseIcon />
               </button>
            </div>
            
            <div className={`modal-body ${isEditing ? 'body-column' : ''}`}>
              {isEditing ? (
                 // --- EDIT MODE ---
                 <div className="edit-container">
                    <h2 className="form-title">Edit {selectedBag.ref}</h2>
                    <form className="bag-form" onSubmit={handleSubmitEdit}>
                       
                       <div className="form-section">
                          <h4 className="section-header">Basic Information</h4>
                          <div className="form-grid">
                             <div className="form-group full-width">
                                <label>Reference (REF) *</label>
                                <input name="ref" value={formData.ref} onChange={handleInputChange} required />
                             </div>
                             <div className="form-group">
                                <label>Color</label>
                                <input name="color" value={formData.color} onChange={handleInputChange} />
                             </div>
                             <div className="form-group">
                                <label>Color Band</label>
                                <input name="colorBand" value={formData.colorBand} onChange={handleInputChange} />
                             </div>
                          </div>
                       </div>

                       <div className="form-section">
                          <h4 className="section-header">Dimensions & Weights</h4>
                          <div className="form-grid">
                             <div className="form-group">
                                <label>Length</label>
                                <input name="long" value={formData.long} onChange={handleInputChange} />
                             </div>
                             <div className="form-group">
                                <label>Width</label>
                                <input name="larg" value={formData.larg} onChange={handleInputChange} />
                             </div>
                             <div className="form-group">
                                <label>Bag Weight</label>
                                <input name="poidSac" value={formData.poidSac} onChange={handleInputChange} />
                             </div>
                             <div className="form-group">
                                <label>MC Weight</label>
                                <input name="poidMC" value={formData.poidMC} onChange={handleInputChange} />
                             </div>
                          </div>
                       </div>

                       <div className="form-section">
                          <h4 className="section-header">Technical Specs</h4>
                          <div className="form-grid">
                             <div className="form-group">
                                <label>DT</label>
                                <input name="DT" value={formData.DT} onChange={handleInputChange} />
                             </div>
                             <div className="form-group">
                                <label>DCH</label>
                                <input name="DCH" value={formData.DCH} onChange={handleInputChange} />
                             </div>
                             <div className="form-group">
                                <label>Bondelette T</label>
                                <input name="bondeletteT" value={formData.bondeletteT} onChange={handleInputChange} />
                             </div>
                             <div className="form-group">
                                <label>Bondelette CH</label>
                                <input name="bondeletteCH" value={formData.bondeletteCH} onChange={handleInputChange} />
                             </div>
                          </div>
                       </div>

                       <div className="form-section">
                          <h4 className="section-header">Visuals</h4>
                          <div className="file-upload-wrapper">
                             <input type="file" id="edit-file" accept="image/*" onChange={handleFileChange} className="file-input-hidden" />
                             <label htmlFor="edit-file" className="file-upload-label">
                                {imageFile ? (
                                  <span className="file-name">{imageFile.name}</span>
                                ) : (
                                  <span><span className="upload-icon">📷</span> Change Image (Optional)</span>
                                )}
                             </label>
                          </div>
                       </div>

                       <div className="form-actions">
                          <button type="button" className="cancel-btn" onClick={() => setIsEditing(false)}>Cancel</button>
                          <button type="submit" className="save-btn">Save Changes</button>
                       </div>
                    </form>
                 </div>
              ) : (
                 // --- VIEW MODE ---
                 <>
                  <div className="modal-sidebar">
                     <div className="image-stage">
                       <img 
                          src={selectedBag.img && selectedBag.img.startsWith('http') ? selectedBag.img : (selectedBag.img ? `/${selectedBag.img}` : '')}
                          alt={selectedBag.ref} 
                          className="modal-hero-image"
                          onError={(e) => {
                            e.target.onerror = null; 
                            e.target.src = `https://ui-avatars.com/api/?name=${selectedBag.ref}&background=f0f0f0&color=aaa&size=500`
                          }}
                        />
                     </div>
                     <div className="item-actions">
                        <button className="action-btn" onClick={() => copyToClipboard(selectedBag.ref)}>
                          <CopyIcon /> Copy Ref
                        </button>
                        <button className="action-btn" onClick={(e) => toggleFavorite(e, selectedBag.id)}>
                          <HeartIcon filled={favorites.has(selectedBag.id)} /> {favorites.has(selectedBag.id) ? 'Saved' : 'Save'}
                        </button>
                        <hr className="divider" />
                        <div className="admin-actions">
                           <button className="action-btn edit-btn" onClick={() => openEditModal(selectedBag)}>
                             <EditIcon /> Edit
                           </button>
                           <button className="action-btn delete-btn" onClick={handleDeleteClick}>
                             <TrashIcon /> Delete
                           </button>
                        </div>
                     </div>
                  </div>
                  
                  <div className="modal-main">
                    <div className="sheet-header">
                      <div className="sheet-meta">
                        <span className="badge-id">ID: {selectedBag.id}</span>
                        <span className="badge-status">Active</span>
                      </div>
                      <h2>{selectedBag.ref}</h2>
                      <div className="sheet-date">Last Updated: {selectedBag.updated_at ? selectedBag.updated_at.split('T')[0] : 'N/A'}</div>
                    </div>
                    
                    <div className="specs-table-container">
                      <h4 className="section-title">Technical Specifications</h4>
                      <div className="specs-grid">
                        <SpecItem label="Base Color" value={selectedBag.color} />
                        <SpecItem label="Band Configuration" value={selectedBag.colorBand} />
                        <SpecItem label="Material Type (T)" value={selectedBag.bondeletteT} />
                        <SpecItem label="Material Type (CH)" value={selectedBag.bondeletteCH} />
                      </div>

                      <h4 className="section-title">Measurements & Metrics</h4>
                      <div className="specs-grid narrow">
                        <SpecItem label="Length (L)" value={selectedBag.long} unit="cm" />
                        <SpecItem label="Width (W)" value={selectedBag.larg} unit="cm" />
                        <SpecItem label="DT" value={selectedBag.DT} />
                        <SpecItem label="DCH" value={selectedBag.DCH} />
                        <SpecItem label="Bag Weight" value={selectedBag.poidSac} unit="g" />
                        <SpecItem label="MC Weight" value={selectedBag.poidMC} unit="g" />
                      </div>
                    </div>
                  </div>
                 </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* --- DELETE CONFIRMATION MODAL --- */}
      {showDeleteConfirm && (
        <div className="modal-overlay delete-overlay" onClick={() => setShowDeleteConfirm(false)}>
           <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
              <div className="confirm-icon">
                <TrashIcon />
              </div>
              <h3>Delete Item?</h3>
              <p>Are you sure you want to delete <strong>{selectedBag?.ref}</strong>? This action cannot be undone.</p>
              <div className="confirm-actions">
                 <button className="cancel-btn" onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
                 <button className="delete-btn-confirm" onClick={confirmDelete}>Yes, Delete</button>
              </div>
           </div>
        </div>
      )}

    </div>
  );
}

const SpecItem = ({ label, value, unit }) => (
  <div className="spec-cell">
    <span className="spec-label">{label}</span>
    <span className="spec-value">{value || '—'}{unit && value ? <small>{unit}</small> : ''}</span>
  </div>
);

export default App;
